import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { SharedModule } from '@/app/shared/shared.module';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TranslateService } from '@ngx-translate/core';
import { PlantationService } from '@/app/core/services/plantation.service';
import { MixpanelTrackerService } from '@/app/core/analytics';
import { ClipboardDirective } from '@/app/shared/directives/clipboard.directive';
import { GroupedPlantationViewModel } from '@/app/proxy/proxies';
import { CropHealthMapTypeLegendModalComponent } from './crop-health-map-type-legend-modal/crop-health-map-type-legend-modal.component';
import { CropHealthService } from '../../../../services/crop-health.service';
import { Subject, takeUntil } from 'rxjs';

export type CropHleathMapTypeKey = 'ndvi' | 'ndmi' | 'inspection' | 'risk';
export interface CropHealthMapType {
  key: CropHleathMapTypeKey;
  label: string;
  labelAbreviation: string;
  value: number;
  description: string;
  disabled?: boolean;
}

@Component({
  selector: 'app-crop-health-map-footer',
  imports: [SharedModule, ClipboardDirective],
  templateUrl: './crop-health-map-footer.component.html',
  styleUrl: './crop-health-map-footer.component.less',
  providers: [DialogService, TranslateService],
})
export class CropHealthMapFooterComponent implements OnInit, OnDestroy {
  @Input() loading = false;
  @Input() selectedMapType: CropHealthMapType;
  @Output() readonly mapSelectionPlaceEvent = new EventEmitter<string>();

  cropHealthMapTypes: CropHealthMapType[] = [
      {
        key: 'ndvi',
        label: 'doktar_app.services.crop_monitoring.ndvi_map_title',
        labelAbreviation: 'NDVI',
        value: 0,
        description: 'doktar_app.services.crop_monitoring.ndvi_map_description',
      },
      {
        key: 'ndmi',
        label: 'doktar_app.services.crop_monitoring.ndmi_map_title',
        labelAbreviation: 'NDMI',
        value: 1,
        description: 'doktar_app.services.crop_monitoring.ndmi_map_description',
      },
      {
        key: 'inspection',
        label: 'doktar_app.services.crop_monitoring.inspection_map_title',
        labelAbreviation: 'Inspection',
        value: 2,
        description: 'doktar_app.services.crop_monitoring.inspection_map_description',
      },
      {
        key: 'risk',
        label: 'doktar_app.services.crop_monitoring.risk_map_title',
        labelAbreviation: 'Risk',
        value: 3,
        description: 'doktar_app.services.crop_monitoring.risk_map_description',
      },
    ]

  showMapOptionsPanel = false;;
  selectedPlantation: GroupedPlantationViewModel;
  private legendModalOpen = false;
  private destroy$ = new Subject<void>();

  constructor(
    private dialogService: DialogService,
    private translateService: TranslateService,
    private plantationService: PlantationService,
    private mixpanelTracker: MixpanelTrackerService,
    public cropHealthService: CropHealthService
  ) {
    window.addEventListener('click', (e: MouseEvent) => {
      const htmlElement = e.target as HTMLElement;
      const isElementInFilter = document.getElementById('map-type-option-btn')?.contains(htmlElement);
      if (!isElementInFilter) {
        this.showMapOptionsPanel = false;
      }
    });
  }
  ngOnInit(): void {
    this.plantationService.selectedPlantation$.subscribe((plantation) => {
      this.selectedPlantation = plantation;

      // Risk Map is available only for premium users
      // this.mapTypes[3].disabled = !plantation.isPremium;
    });

    // Mixpanel: if legend/info modal is open, any map type change is attributed to that modal.
    this.cropHealthService.selectedMapType$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.legendModalOpen) {
          return;
        }
        this.mapSelectionPlaceEvent.emit('Information modal');
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private attachLegendModalCloseHandlers(ref: DynamicDialogRef): void {
    const markClosed = () => {
      this.legendModalOpen = false;
    };

    ref.onClose.subscribe(markClosed);
    // Some close paths may trigger onDestroy without onClose.
    ref.onDestroy?.subscribe(markClosed);
  }

  openMapTypeLegend() {
    this.mixpanelTracker.trackServicesMapInformationModalOpened();
    this.legendModalOpen = true;
    const ref: DynamicDialogRef = this.dialogService.open(CropHealthMapTypeLegendModalComponent, {
      header: this.translateService.instant('doktar_app.services.crop_monitoring.map_type_legend_question'),
      closable: true,
      styleClass: 'no-overflow',
      modal: true, // ✅ ensures backdrop is shown
      dismissableMask: true, // Optional: allows clicking outside to close
      inputValues: {
        selectedMapType: this.selectedMapType?.value,
        cropHealthMapTypes: this.cropHealthMapTypes
      },
    });
    this.attachLegendModalCloseHandlers(ref);
  }
}
